import type { ReactNode } from 'react';

import { Stamp } from './Stamp.js';

interface FaqItem {
  question: string;
  answer: ReactNode;
}

const FAQ: ReadonlyArray<FaqItem> = [
  {
    question: 'Como exportar o PDF do meu perfil do LinkedIn?',
    answer: (
      <>
        <p>
          No app do LinkedIn (desktop), entre no seu próprio perfil, clique no botão{' '}
          <strong>Mais</strong> que fica logo abaixo da sua foto, e escolha{' '}
          <strong>Salvar como PDF</strong>. O LinkedIn vai gerar e baixar um arquivo com tudo o
          que está visível no seu perfil (headline, sobre, experiências, skills, certs,
          formação).
        </p>
        <p className="mt-3">
          No celular não dá. Você precisa usar o LinkedIn pelo navegador do computador, ou
          pelo navegador do celular forçando "versão para computador" nas configurações.
        </p>
        <p className="mt-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
          Importante: o PDF é processado em memória aqui no SmartApply e descartado quando sua
          sessão expira. Nada do conteúdo do seu perfil é salvo no nosso banco.
        </p>
      </>
    ),
  },
  {
    question: 'Por que preciso de uma chave de IA? Quanto vou pagar?',
    answer: (
      <>
        <p>
          O SmartApply usa um modelo de linguagem (Gemini, Claude ou GPT) pra fazer a análise
          do seu perfil e gerar os rascunhos de post. Em vez de cobrar uma mensalidade
          embutindo nosso custo de IA, a gente segue o modelo BYOK (Bring Your Own Key): você
          conecta a sua chave do provider que preferir.
        </p>
        <p className="mt-3">
          Recomendamos o <strong>Google Gemini</strong>: o tier gratuito dá 1500 chamadas por
          dia, mais que o suficiente pra um uso normal, e não exige cartão de crédito pra
          começar.
        </p>
        <p className="mt-3">
          Se você já é cliente do Claude (Anthropic) ou ChatGPT API (OpenAI), pode usar a sua
          chave existente. Ambos cobram por uso (alguns centavos por análise), sem
          mensalidade.
        </p>
      </>
    ),
  },
  {
    question: 'Como obter a chave do Google Gemini (recomendado, grátis)',
    answer: (
      <>
        <p>Em três passos:</p>
        <ol className="mt-3 ml-4 list-decimal space-y-2 text-[var(--ink-secondary)]">
          <li>
            Acesse{' '}
            <a
              href="https://aistudio.google.com/apikey"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            >
              aistudio.google.com/apikey
            </a>{' '}
            e entre com sua conta Google.
          </li>
          <li>
            Clique em <strong>Create API key</strong>. Aceite os termos quando aparecerem.
          </li>
          <li>
            Copie a chave que aparece (começa com <code className="font-mono text-xs">AIza...</code>
            ) e cole aqui no SmartApply na tela de configuração do provider.
          </li>
        </ol>
        <p className="mt-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
          Você não precisa adicionar cartão. O tier gratuito do Gemini é generoso o suficiente
          pra dezenas de análises por dia.
        </p>
      </>
    ),
  },
  {
    question: 'Como obter a chave do Anthropic Claude',
    answer: (
      <>
        <p>
          O Claude da Anthropic é uma alternativa premium ao Gemini. Não tem tier
          gratuito permanente, mas oferece cinco dólares de crédito inicial pra novos usuários
          (suficiente pra centenas de análises).
        </p>
        <ol className="mt-3 ml-4 list-decimal space-y-2 text-[var(--ink-secondary)]">
          <li>
            Crie conta em{' '}
            <a
              href="https://console.anthropic.com/"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            >
              console.anthropic.com
            </a>
            .
          </li>
          <li>
            No menu lateral, clique em <strong>API Keys</strong> e depois em{' '}
            <strong>Create Key</strong>.
          </li>
          <li>
            Copie a chave (começa com <code className="font-mono text-xs">sk-ant-...</code>) e
            cole aqui no SmartApply.
          </li>
        </ol>
      </>
    ),
  },
  {
    question: 'Como obter a chave do OpenAI (ChatGPT)',
    answer: (
      <>
        <p>
          O OpenAI exige cadastrar cartão antes de gerar a chave, mesmo que você só vá usar o
          tier gratuito. Custa alguns centavos por análise.
        </p>
        <ol className="mt-3 ml-4 list-decimal space-y-2 text-[var(--ink-secondary)]">
          <li>
            Crie conta em{' '}
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            >
              platform.openai.com/api-keys
            </a>
            .
          </li>
          <li>
            Adicione um método de pagamento na seção <strong>Billing</strong> (a OpenAI exige
            mesmo pro tier inicial).
          </li>
          <li>
            Volte em <strong>API Keys</strong> e clique em <strong>Create new secret key</strong>.
          </li>
          <li>
            Copie a chave (começa com <code className="font-mono text-xs">sk-...</code>) e cole
            aqui. Importante: a OpenAI só mostra a chave uma vez, então salve em algum lugar
            seguro também.
          </li>
        </ol>
      </>
    ),
  },
  {
    question: 'É seguro deixar minha chave de IA aqui?',
    answer: (
      <>
        <p>
          Sua chave fica na sua sessão em memória do servidor, não no nosso banco de dados.
          Quando você sai ou a sessão expira (uma hora de inatividade), a chave é descartada e
          a gente perde acesso a ela completamente.
        </p>
        <p className="mt-3">
          Nada do conteúdo das suas chamadas pra IA fica nos nossos logs. Sua chave nunca é
          impressa em log, nunca é versionada em commit, nunca é compartilhada com terceiros.
        </p>
        <p className="mt-3">
          Se quiser ser ainda mais seguro: gere uma chave restrita só pra usar no SmartApply,
          e revogue ela quando parar de usar. Todos os providers (Google, Anthropic, OpenAI)
          deixam você revogar chaves a qualquer momento sem afetar o resto da sua conta.
        </p>
      </>
    ),
  },
  {
    question: 'Vocês acessam minha conta do LinkedIn?',
    answer: (
      <p>
        Não. A gente nunca pede seu login do LinkedIn, nunca acessa sua conta, nunca scrapeia o
        feed nem suas conexões. Tudo o que o SmartApply sabe sobre o seu perfil vem do PDF que
        você mesmo exporta e sobe na ferramenta.
      </p>
    ),
  },
  {
    question: 'O que acontece com meu PDF depois da análise?',
    answer: (
      <>
        <p>
          O PDF é parseado em memória no servidor, virando uma estrutura de dados que a IA
          consegue ler. O arquivo bruto é descartado imediatamente. A estrutura parseada vive
          na sua sessão por uma hora (pra você poder rodar análises diferentes sem precisar
          subir o PDF de novo) e some quando a sessão expira.
        </p>
        <p className="mt-3">
          Análises podem ser salvas no histórico, mas só se você clicar explicitamente em
          "salvar análise". Mesmo aí, fica salva ligada à sua conta e ninguém além de você
          acessa.
        </p>
      </>
    ),
  },
  {
    question: 'Posso deletar minha conta e tudo o que está salvo?',
    answer: (
      <p>
        Sim. Na tela de Segurança da app autenticada tem a opção de apagar tudo. Análises
        salvas, drafts de post, configurações e a própria conta são removidos do banco. O
        processo é imediato e irreversível, então pense duas vezes antes de confirmar.
      </p>
    ),
  },
];

export function FaqSection() {
  return (
    <section id="faq" className="border-b border-[var(--border-subtle)] py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1fr_1.3fr]">
        <header className="lg:sticky lg:top-24 lg:self-start">
          <Stamp>perguntas frequentes</Stamp>
          <h2 className="mt-4 font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[44px]">
            Dúvidas práticas
            <span className="text-[var(--accent-saffron)]">.</span>
          </h2>
          <p className="mt-3 font-sans text-base text-[var(--ink-secondary)]">
            As partes que costumam travar gente na primeira vez. PDF do LinkedIn, chave da IA,
            privacidade. Se faltou alguma, manda lá em <a
              href="#suggest"
              className="text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            >
              sugerir
            </a>
            .
          </p>
        </header>

        <ul className="divide-y divide-[var(--border-subtle)]">
          {FAQ.map((item) => (
            <li key={item.question}>
              <details className="group">
                <summary className="flex cursor-pointer items-baseline justify-between gap-4 py-5 list-none [&::-webkit-details-marker]:hidden">
                  <span className="font-serif text-lg font-medium text-[var(--ink-primary)] transition-colors group-hover:text-[var(--accent-saffron)]">
                    {item.question}
                  </span>
                  <span
                    aria-hidden
                    className="shrink-0 font-mono text-xl leading-none text-[var(--ink-tertiary)] transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <div className="pb-6 font-sans text-[15px] leading-relaxed text-[var(--ink-secondary)]">
                  {item.answer}
                </div>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
